"use client";

import React, { createContext, useContext } from "react";

import { isMockMode } from "@/lib/runtimeMode";

export const forceToBoolean = (str: string | null | undefined) =>
	Boolean(Number(str));

/**
 * Mock mode serves GraphQL from the app itself, so the client talks to a relative
 * path instead of a jasper-api host. Everything downstream — the upload link, the
 * X-Authorization header, the error link, the cache — is unchanged, which is the
 * point: mock mode exercises the real client rather than a substitute one.
 */
const MOCK_GRAPHQL_URI = "/api/mock/graphql";

const jasperApiUri =
	process.env.NEXT_PUBLIC_API_SERVICE_DOMAIN &&
	`${process.env.NEXT_PUBLIC_API_SERVICE_PROTOCOL ?? "https"}://${process.env.NEXT_PUBLIC_API_SERVICE_DOMAIN}/query`;

const variables = {
	graphQLApiUri: isMockMode() ? MOCK_GRAPHQL_URI : jasperApiUri,
	cookieDomain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
	runtimeEnvironment: process.env.NEXT_PUBLIC_RUNTIME_ENVIRONMENT,
	releaseId: process.env.NEXT_PUBLIC_RELEASE_ID,
	sentryDSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
	runningDemos: forceToBoolean(process.env.NEXT_PUBLIC_RUNNING_DEMOS),
	elevenLabsAgentId: process.env.NEXT_PUBLIC_ELEVENLABS_AGENT_ID,
} as const;

const EnvironmentVariablesContext = createContext<typeof variables>(variables);

export function useEnvironmentVariables() {
	const context = useContext(EnvironmentVariablesContext);

	if (!context) {
		throw new Error(
			"useEnvironmentVariables should always be used within an EnvironmentVariablesProvider",
		);
	}

	return context;
}

export const EnvironmentVariablesProvider = ({
	children,
	...rest
}: {
	children: React.ReactNode;
}) => (
	<EnvironmentVariablesContext.Provider value={variables} {...rest}>
		{children}
	</EnvironmentVariablesContext.Provider>
);
